import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Phone, Mail, MessageCircle, Bot, Clock, Check, X, ChevronRight } from 'lucide-react';
import { useSeo } from '@/lib/seo';
import SupportForm from '@/components/support/SupportForm';
import { useOpeningHours } from '@/hooks/useOpeningHours';
import { useChat } from '@/lib/ChatContext';

const DEFAULTS = {
  website_url: 'https://motionreality.co.uk/customer-service',
  email: 'sales@motionreality.co.uk',
  country_code: 'GB',
  phone_number: '+44 151 528 3001',
  chatbot_available: false,
  hours: 'Mon–Fri 9am–6pm · Sat 10am–4pm',
};

export default function CustomerService() {
  const { data: settingsList = [] } = useQuery({
    queryKey: ['customer-service-settings'],
    queryFn: () => base44.entities.CustomerServiceSettings.list(),
  });
  const { storefront, formatHours, isOpenNow } = useOpeningHours();
  const { available: chatAvailable, open: openChat } = useChat();
  const hoursLabel = storefront ? formatHours(storefront) : 'Mon–Sat 9am–6pm';
  const open = !!storefront && isOpenNow(storefront);
  const s = {
    ...DEFAULTS,
    ...((settingsList || []).find((x) => x.setting_key === 'global') || {}),
  };

  useSeo({
    title: 'Customer Service — Motion Reality Sim Racing',
    description: 'Customer service contact details for Motion Reality: phone, email, opening hours and support channels.',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Motion Reality Sim Racing',
      url: s.website_url,
      email: s.email,
      telephone: s.phone_number,
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Unit 9, Croft Business Park, Carrock Road',
        addressLocality: 'Bromborough',
        addressRegion: 'Merseyside',
        postalCode: 'CH62 3RA',
        addressCountry: s.country_code,
      },
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        telephone: s.phone_number,
        email: s.email,
        areaServed: s.country_code,
        availableLanguage: ['English'],
      },
    },
  });

  const telHref = 'tel:' + (s.phone_number || '').replace(/[^\d+]/g, '');
  const whatsappHref = 'https://wa.me/447411254009';
  const scrollToForm = (e) => {
    e.preventDefault();
    document.getElementById('support-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      {/* Header */}
      <section className="py-16 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-3xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-primary text-xs font-bold tracking-widest mb-3">SUPPORT</p>
            <h1 className="text-white font-black text-4xl md:text-5xl uppercase leading-tight mb-3">
              Customer Service
            </h1>
            <p className="text-white/40 text-sm">
              How to reach the Motion Reality team for help with orders, bookings, returns or product advice.
            </p>
          </motion.div>
        </div>
      </section>

      {/* 3-column channel grid */}
      <section className="px-6 lg:px-10 py-10">
        <div className="max-w-5xl mx-auto w-full grid grid-cols-1 md:grid-cols-3 items-stretch gap-6">
          {/* Card 1 — Instant Chat */}
          <div className="bg-[#161616] border border-primary/30 p-6 h-full flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Bot className="w-4 h-4 text-primary" />
                <p className="text-primary text-[10px] font-bold tracking-widest">INSTANT CHAT</p>
              </div>
              <p className="text-white/50 text-xs mb-5">Automated answers in seconds, or message us on WhatsApp.</p>
            </div>
            <div className="flex flex-col gap-3">
              <button
                onClick={openChat}
                disabled={!s.chatbot_available || !chatAvailable}
                className="inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white text-xs font-bold tracking-widest px-4 py-2.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Bot className="w-4 h-4" /> OPEN CHAT BOT
              </button>
              <div className="min-h-[3.25rem] flex items-center justify-center">
                <a
                  href={whatsappHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-1.5 text-[#25D366] hover:text-[#1ebe5d] text-xs font-medium transition-colors"
                >
                  <WhatsAppIcon className="w-3.5 h-3.5" /> Or chat on WhatsApp →
                </a>
              </div>
            </div>
          </div>

          {/* Card 2 — Phone & Hours */}
          <div className="bg-[#161616] border border-primary/30 p-6 h-full flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Phone className="w-4 h-4 text-primary" />
                <p className="text-primary text-[10px] font-bold tracking-widest">PHONE &amp; HOURS</p>
              </div>
              <p className="text-white/50 text-xs mb-5">Speak to the team during opening hours.</p>
            </div>
            <div className="flex flex-col gap-3">
              <a href={telHref} className="inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white text-xs font-bold tracking-widest px-4 py-2.5 transition-colors">
                <Phone className="w-3.5 h-3.5" /> 0151 528 3001
              </a>
              <div className="min-h-[3.25rem] flex flex-col justify-center gap-1">
                <div className="flex items-center gap-2 text-white/70 text-xs">
                  <Clock className="w-3.5 h-3.5 text-primary" />
                  <span>{hoursLabel}</span>
                </div>
                <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold tracking-widest ${open ? 'text-green-400' : 'text-white/30'}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${open ? 'bg-green-400' : 'bg-white/30'}`} />
                  {open ? 'OPEN NOW' : 'CLOSED NOW'}
                </span>
              </div>
            </div>
          </div>

          {/* Card 3 — Support Ticket / Email */}
          <div className="bg-[#161616] border border-primary/30 p-6 h-full flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Mail className="w-4 h-4 text-primary" />
                <p className="text-primary text-[10px] font-bold tracking-widest">SUPPORT TICKET / EMAIL</p>
              </div>
              <p className="text-white/50 text-xs mb-5">Send us a message and we'll reply by email.</p>
            </div>
            <div className="flex flex-col gap-3">
              <button
                onClick={scrollToForm}
                className="inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white text-xs font-bold tracking-widest px-4 py-2.5 transition-colors"
              >
                <Mail className="w-4 h-4" /> LOG A TICKET
              </button>
              <div className="min-h-[3.25rem] flex items-center justify-center">
                <a
                  href="mailto:support@motionreality.co.uk"
                  className="text-white/60 hover:text-primary text-xs font-medium transition-colors break-all text-center"
                >
                  support@motionreality.co.uk
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ prompt */}
      <section className="px-6 lg:px-10 pt-10">
        <div className="max-w-5xl mx-auto w-full bg-[#161616] border border-primary/30 p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-center sm:text-left">
            <p className="text-primary text-[10px] font-bold tracking-widest mb-1">QUICK ANSWERS</p>
            <p className="text-white text-sm font-bold">Check our FAQ before reaching out</p>
            <p className="text-white/40 text-xs mt-1">Sessions, pricing, age groups, competitions and more.</p>
          </div>
          <Link
            to="/faq"
            className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-widest px-6 py-3 transition-colors shrink-0"
          >
            VIEW FAQ <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </section>

      {/* Support request form */}
      <section id="support-form" className="px-6 lg:px-10 pt-12 pb-16 scroll-mt-24">
        <div className="max-w-5xl mx-auto w-full">
          <div className="mb-4">
            <h2 className="text-white font-black text-2xl md:text-3xl uppercase">Send us a support request</h2>
            <p className="text-white/40 text-sm mt-1">Tell us what you need and our team will get back to you by email.</p>
          </div>
          <SupportForm />
        </div>
      </section>
    </div>
  );
}

function WhatsAppIcon({ className = '' }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
    </svg>
  );
}